import React, { FC, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { SkeletonTheme } from 'react-loading-skeleton';

import { buildSkeletonTheme } from 'components/layout/skeleton-theme/util';
import Toggle from 'components/toggle';
import { ICartRewardsDropdown } from 'pages/cart/cart-reward-dropdown/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ILoyaltyCategory } from 'state/loyalty/types';
import { useThRewardsManagementContext } from 'state/th-rewards-management';

import { useBankMyRewardsTierId, useRewardSelectionModal } from './hooks';
import { RewardTier, RewardTierSkeleton } from './reward-tier';
import { RewardsManagementSkeleton } from './rewards-management.skeleton';
import { Container, SubTitle, Title, TitleContainer } from './rewards-management.styled';

type LoyaltyCategoryOption = ILoyaltyCategory['options'][number];

/**
 * @name RewardsManagement
 * @purpose handles the TH Loyalty Rewards Tier selection UX
 * This component is the entry point for the Toggle UX.
 */

const RewardsManagement: FC<ICartRewardsDropdown> = ({ skeletonTheme, ...props }) => {
  const enableTHRewardsTierToggleUI = useFlag(LaunchDarklyFlag.ENABLE_TH_REWARD_TIER_TOGGLE_UI);
  const { formatMessage } = useIntl();
  const { bankMyRewardTierId } = useBankMyRewardsTierId();

  const [isRedeemingPoints, setIsRedeemingPoints] = useState<boolean>();

  const { onChangeRewardTier, selectedRewardTier, selectedRewardTierId, updatingRewardTier } =
    useThRewardsManagementContext();

  const [RewardSelectionModal, { openRewardsModal }] = useRewardSelectionModal();

  const handleToggle = (e: any) => {
    const { checked } = e.target;
    setIsRedeemingPoints(checked);
    if (checked) {
      onChangeRewardTier('ON');
    } else {
      onChangeRewardTier(bankMyRewardTierId);
    }
  };

  useEffect(() => {
    if (!updatingRewardTier && selectedRewardTierId != null && bankMyRewardTierId != null) {
      setIsRedeemingPoints(selectedRewardTierId !== bankMyRewardTierId);
    }
  }, [bankMyRewardTierId, selectedRewardTierId, updatingRewardTier]);

  /**
   * NOTE: This UI is being rolled out slowly behind this flag
   * @TODO: remove this check once the flag is fully rolled out.
   */
  if (!enableTHRewardsTierToggleUI) {
    return null;
  }

  const shouldShowRewardTierLoader =
    updatingRewardTier || isRedeemingPoints === null || (!selectedRewardTier && isRedeemingPoints);
  let RewardTierDisplay = (
    <RewardTier
      openChangeRewardsModal={openRewardsModal}
      selectedTier={selectedRewardTier}
      data-testid="reward-tier"
    />
  );
  if (shouldShowRewardTierLoader) {
    RewardTierDisplay = <RewardTierSkeleton {...props} />;
  } else if (isRedeemingPoints === false) {
    RewardTierDisplay = (
      <SubTitle data-testid="banking-points">{formatMessage({ id: 'bankingPoints' })}</SubTitle>
    );
  }

  return (
    <>
      <Container {...props}>
        <SkeletonTheme {...buildSkeletonTheme(skeletonTheme)}>
          <TitleContainer>
            <Title>{formatMessage({ id: 'redeemMyPoints' })}</Title>
            {typeof isRedeemingPoints === 'boolean' ? (
              <Toggle
                checked={isRedeemingPoints}
                onChange={handleToggle}
                data-testid="rewards-management-toggle"
              />
            ) : (
              <RewardsManagementSkeleton />
            )}
          </TitleContainer>
          {RewardTierDisplay}
        </SkeletonTheme>
      </Container>
      <RewardSelectionModal />
    </>
  );
};

export default RewardsManagement;
